import api from '@/services/api';
import type { PricingPayload } from '../types';

// Time-limited cache. Backend config edits (model rename, infra rate
// tweaks) must propagate within 60s — otherwise users sit on stale
// data until they reload the entire tab. Pricing endpoint is fast
// (<50ms), no need to be aggressive here.
const CACHE_TTL_MS = 60_000;
const SESSION_CACHE_KEY = 'cost_simulator_pricing';
let cached: PricingPayload | null = null;
let cachedAt = 0;
let inflight: Promise<PricingPayload> | null = null;

// Hydrate from sessionStorage on module load so the FIRST fetchPricing
// call after a page reload returns synchronously. Without this, every
// page reload showed "Cost Simulator loading..." for the network round
// trip even though the data was already known good a second ago.
try {
  const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
  if (raw) {
    const { data, ts } = JSON.parse(raw) as { data: PricingPayload; ts: number };
    if (data && typeof ts === 'number' && Date.now() - ts < CACHE_TTL_MS) {
      cached = data;
      cachedAt = ts;
    }
  }
} catch {
  /* corrupt cache — ignore */
}

export async function fetchPricing(force = false): Promise<PricingPayload> {
  const fresh = cached && (Date.now() - cachedAt) < CACHE_TTL_MS;
  if (fresh && !force) return cached!;
  if (inflight) return inflight;
  inflight = api.get<{ success: boolean; pricing: PricingPayload }>('/cost-simulator/pricing')
    .then(res => {
      cached = res.data.pricing;
      cachedAt = Date.now();
      inflight = null;
      try {
        sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify({ data: cached, ts: cachedAt }));
      } catch {
        /* quota / sandbox — non-fatal */
      }
      return cached;
    })
    .catch(err => {
      inflight = null;
      throw err;
    });
  return inflight;
}
